import React, { useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Search, X, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults(null);
      setTimeout(() => inputRef.current?.focus(), 200);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (q.trim().length < 2) { setResults(null); return; }
      setLoading(true);
      try { const r = await api.search(q.trim()); setResults(r); } catch { setResults(null); } finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const totalHits = results
    ? results.articles.length + results.news.length + results.interviews.length + results.magazines.length
    : 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          className="fixed inset-0 z-[80] bg-paper/98 backdrop-blur-xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
        >
          <div className="max-w-[900px] mx-auto px-5 md:px-8 pt-24 md:pt-32">
            <div className="flex items-center justify-between mb-8">
              <div className="overline overline-muted">Global Search</div>
              <button
                data-testid="search-close"
                onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-full border border-ink-line text-ink-soft hover:text-ink"
                aria-label="Close search"
              >
                <X size={16} strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex items-center gap-4 border-b border-ink pb-4">
              <Search size={22} strokeWidth={1.2} className="text-ink" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles, news, interviews, magazines…"
                className="w-full bg-transparent outline-none font-display text-2xl md:text-4xl text-ink placeholder:text-ink-faint font-light"
              />
            </div>

            <div className="mt-8 max-h-[65vh] overflow-y-auto pr-1">
              {loading && <div className="text-ink-soft text-sm">Searching…</div>}
              {!loading && q.trim().length >= 2 && results && totalHits === 0 && (
                <div className="text-ink-soft text-sm" data-testid="search-empty">No results found for {"\u201C"}{q}{"\u201D"}</div>
              )}
              {results && totalHits > 0 && (
                <div className="space-y-8" data-testid="search-results">
                  <ResultGroup label="Articles" items={results.articles} to="/articles" />
                  <ResultGroup label="News" items={results.news} to="/news" />
                  <ResultGroup label="Interviews" items={results.interviews} to="/interviews" titleField="name" />
                  <ResultGroup label="Magazines" items={results.magazines} to="/magazine" />
                </div>
              )}
              {q.trim().length < 2 && (
                <div className="mt-6">
                  <div className="overline overline-muted mb-4">Suggested</div>
                  <div className="flex flex-wrap gap-2">
                    {["Artificial Intelligence", "Cybersecurity", "Leadership", "Startups", "Healthcare", "Finance", "Energy"].map((c) => (
                      <button
                        key={c}
                        onClick={() => setQ(c)}
                        data-testid={`search-suggest-${c.toLowerCase().replace(/\s+/g, "-")}`}
                        className="text-xs px-3 py-1.5 border border-ink-line text-ink-soft hover:text-ink hover:border-ink transition-colors rounded-full"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const ResultGroup = ({ label, items, to, titleField = "title" }) => {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid={`search-group-${label.toLowerCase()}`}>
      <div className="overline overline-muted mb-3">{label} · {items.length}</div>
      <ul className="divide-y divide-ink-line">
        {items.slice(0, 6).map((item) => (
          <li key={item.id || item.slug}>
            <Link
              to={`${to}/${item.slug}`}
              className="group flex items-center justify-between gap-4 py-3 hover:pl-2 transition-all"
              data-testid={`search-result-${item.slug}`}
            >
              <div>
                <div className="text-ink font-display text-lg md:text-xl leading-tight">{item[titleField]}</div>
                {item.category && <div className="text-[11px] font-mono-editorial uppercase tracking-[0.18em] text-ink-muted mt-1">{item.category}</div>}
              </div>
              <ArrowUpRight size={18} className="text-ink-muted group-hover:text-ink transition-colors" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SearchOverlay;
